/**
 * Create-a-club screen (/groups/new). Name + sport + format → creates the club
 * (you become admin) → the payoff: a shareable invite link.
 */
import { useState } from "react";

import { createClub, type CreatedClub } from "../api";

const SPORTS: { key: string; label: string }[] = [
  { key: "tennis", label: "Tennis" },
  { key: "pickleball", label: "Pickleball" },
  { key: "padel", label: "Padel" },
];

export function CreateClubScreen({
  onCreated,
  onCancel,
}: {
  onCreated: (id: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [sport, setSport] = useState("tennis");
  const [format, setFormat] = useState<"singles" | "doubles" | "mixed">("doubles");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedClub | null>(null);

  const padelLocked = sport === "padel"; // padel is doubles-only
  const effFormat = padelLocked ? "doubles" : format;

  async function submit() {
    const trimmed = name.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    setError(null);
    try {
      const club = await createClub({ name: trimmed, sport, default_format: effFormat });
      setCreated(club);
    } catch (e) {
      setError(String((e as Error).message));
    } finally {
      setSaving(false);
    }
  }

  if (created) {
    return <Shared club={created} onDone={() => onCreated(created.id)} />;
  }

  return (
    <div className="max-w-md mx-auto px-5 pt-10">
      <button
        onClick={onCancel}
        className="font-mono text-[11px] uppercase tracking-wider text-ink/55 hover:text-ink mb-6"
      >
        ← Back
      </button>
      <h1 className="font-display font-bold text-3xl tracking-tight mb-1">Start a club</h1>
      <p className="text-ink/60 mb-8">
        Spin up your own club and invite your friends. You'll be the admin.
      </p>

      <label className="block mb-5">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/55">
          Club name
        </span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          placeholder="e.g. Tuesday Night Tennis"
          className="mt-1.5 w-full rounded-md border border-ink/20 px-3 py-2.5 text-base focus:outline-none focus:border-ink/50 bg-bg"
        />
      </label>

      <div className="mb-5">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/55">
          Sport
        </span>
        <div className="mt-1.5 flex gap-2">
          {SPORTS.map((s) => (
            <button
              key={s.key}
              onClick={() => setSport(s.key)}
              className={
                "flex-1 px-3 py-2.5 rounded-md font-display font-bold text-sm cs-tap border-2 " +
                (sport === s.key ? "bg-ink text-bg border-ink" : "border-ink/15 hover:border-ink/40")
              }
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-8">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/55">
          Default format
        </span>
        <div className="mt-1.5 flex gap-2">
          {(["singles", "doubles", "mixed"] as const).map((f) => (
            <button
              key={f}
              disabled={padelLocked && f !== "doubles"}
              onClick={() => setFormat(f)}
              className={
                "flex-1 px-3 py-2.5 rounded-md font-display font-bold text-sm cs-tap border-2 capitalize disabled:opacity-30 " +
                (effFormat === f ? "bg-accent text-ink border-ink" : "border-ink/15 hover:border-ink/40")
              }
            >
              {f}
            </button>
          ))}
        </div>
        <div className="text-xs text-ink/45 mt-1.5">
          {padelLocked
            ? "Padel is doubles-only."
            : "Mixed = a club that plays both; pick singles or doubles per game."}
        </div>
      </div>

      {error && <div className="text-sm text-[#C84B31] mb-4">{error}</div>}

      <button
        onClick={() => void submit()}
        disabled={saving || !name.trim()}
        className="w-full px-5 py-3 bg-ink text-bg font-display font-bold text-base cs-tap rounded-md disabled:opacity-40"
      >
        {saving ? "Creating…" : "Create club"}
      </button>
    </div>
  );
}

function Shared({ club, onDone }: { club: CreatedClub; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/join/${club.join_code}`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* ignore */
    }
  }
  async function share() {
    if (navigator.share) {
      try {
        await navigator.share({ title: club.name, text: `Join ${club.name} on ClubBot`, url });
      } catch {
        /* user cancelled */
      }
    } else {
      void copy();
    }
  }

  return (
    <div className="max-w-md mx-auto px-5 pt-12 text-center">
      <div className="text-4xl mb-3">🎉</div>
      <h1 className="font-display font-bold text-3xl tracking-tight">
        {club.name} is live.
      </h1>
      <p className="text-ink/60 mt-2 mb-8">
        Share this link to invite friends — anyone who taps it joins your club.
      </p>

      <div className="flex items-stretch border-2 border-ink rounded-md overflow-hidden text-left">
        <div className="flex-1 min-w-0 px-4 py-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/55">
            Invite link
          </div>
          <div className="font-mono text-sm truncate">{url}</div>
        </div>
        <button
          onClick={copy}
          className="bg-accent text-ink px-5 font-display font-bold text-sm border-l-2 border-ink cs-tap hover:brightness-105 whitespace-nowrap"
        >
          {copied ? "Copied" : "Copy link"}
        </button>
      </div>

      <div className="flex gap-2 mt-4">
        <button
          onClick={() => void share()}
          className="flex-1 px-5 py-3 bg-ink text-bg font-display font-bold text-sm cs-tap rounded-md"
        >
          Share
        </button>
        <button
          onClick={onDone}
          className="flex-1 px-5 py-3 border-2 border-ink/20 font-display font-bold text-sm cs-tap rounded-md"
        >
          Go to club →
        </button>
      </div>
    </div>
  );
}
